import { VCPM } from '../models/project.model';
import { VcfDomain } from '../models/vcf-domain.model';
import { DataProvider } from '../models/data-provider.model';
import { PcsSizingEngineRequest } from '../models/pcs-sizing-engine-request.model';
import { PcsSizingEngineResponse } from '../models/pcs-sizing-engine-response.model';
import { AppConstants } from '../models/app-constants.model';
import { WORKLOAD_DOMAIN_TYPE, HARDWARE_PREFERENCE } from '../models/pcsizer.enums';
import { VersionUtils } from './version-utils';
import { isWorkloadStretchedByInput } from '../transforms/workload-stretch.transform';

/**
 * Resolved by the caller from Angular-only sources (NGXS store, DomainInputConfigurationFactoryService)
 * before calling the pure functions below. See plan Section 2.1 / Gap H.
 */
export interface SizingRequestContext {
  /** From ProjectDetailsState.getWldComponents. */
  wldComponents: DataProvider.Component[];
  /** From ProjectDetailsState.getManagementDomainComponents. */
  mgmtDomainComponents: DataProvider.Component[];
  /**
   * From DomainInputConfigurationFactoryService.getConfiguration(product, version, architecture,
   * DOMAIN_TYPE.ManagementDomain)?.configuration - the factory itself is Angular-free (Gap H), but
   * its 6 backing input-config data classes (~2,500 LOC total) are out of scope for this slice, so
   * the caller resolves this ahead of time rather than the pure function calling the factory.
   */
  managementDomainInputConfig: VcfDomain.Group | null;
}

/**
 * Model gap (not fixed): PcsSizingEngineRequest.Requirement doesn't declare
 * workloadDomainType, but the WLD payload always sends it (the MGMT payload doesn't set this
 * field). See "Behavior anomalies observed" in the S2 task report.
 */
interface RequirementWithWorkloadDomainType extends PcsSizingEngineRequest.Requirement {
  workloadDomainType?: string;
}

/**
 * Ported as-is from PCSSizingEngineRequestAdapterService.getWorkloadDomainRecommendationRequest
 * (pcs-sizing-engine-request-adapter.service.ts:84-194).
 */
export function getWorkloadDomainRecommendationRequest(
  project: VCPM.Project,
  context: SizingRequestContext,
): PcsSizingEngineRequest.DesignRequest | null {
  let designRequest: PcsSizingEngineRequest.DesignRequest | null = null;
  if (project.data.workloadDomains && Array.isArray(project.data.workloadDomains) && project.data.workloadDomains.length > 0) {
    const domainRequirements: RequirementWithWorkloadDomainType[] = [];
    // loop workload domains
    for (let dIndex = 0; dIndex < project.data.workloadDomains.length; dIndex++) {
      const domain = project.data.workloadDomains[dIndex];
      if (!domain.workloadList?.length) continue;
      const workloads: PcsSizingEngineRequest.Workload[] = [];

      //Workload Request Objects
      // loop workloads
      for (let index = 0; index < project.data.workloadDomains[dIndex].workloadList.length; index++) {
        const workload = project.data.workloadDomains[dIndex].workloadList[index];
        const isDhci = workload.vcfDeploymentInputs?.deploymentType === 'dHCI';
        // dHCI (vSAN Max): CPU headroom / compute growth-factor is a Compute-cluster
        // concern; vSAN Operations Reserve and Host Rebuild Reserve are storage-capacity
        // reservations and swap-space accounting belong to StorageClusterConfiguration. This split matches the two
        // config interfaces' own field sets (see ComputeClusterConfiguration/
        // StorageClusterConfiguration in project.model.ts), not an arbitrary choice.
        const computeConfig = isDhci ? workload.computeClusterConfiguration : workload.clusterConfiguration;
        const storageMarginConfig = isDhci ? workload.storageClusterConfiguration : workload.clusterConfiguration;
        // vSAN Max: Storage can be independently stretched from Compute (one-directional lock - see
        // enforceStorageStretchLock() in workload-configuration.component.ts), so reserve suppression
        // below must key off Storage's OWN clusterType, not Compute's - operationsReserve/
        // hostRebuildReserve are storage-only fields and the sizing-engine rejects them (code 110013)
        // whenever the STORAGE cluster is stretched, regardless of Compute's layout. For HCI,
        // storageMarginConfig === computeConfig (same clusterConfiguration), so this is a no-op change.
        const isStorageStretched = storageMarginConfig?.clusterType === 'stretched';
        // 'use-existing-cluster' is a wrapper, never a terminal mode - the workload's own mirror
        // deliberately keeps this sentinel (see resolveEffectiveHardwareConfiguration's doc comment)
        // so chassis-building/hardware-preference must resolve through to the referenced cluster
        // entity's real mode. Computed unconditionally (cheap, no-op for a dHCI workload, whose HCI
        // hardwareConfiguration/computeCluster are never meaningfully set) so it's available
        // for both branches below without duplicating the resolution call.
        const effectiveHardwareConfiguration = resolveEffectiveHardwareConfiguration(
          workload.hardwareConfiguration, domain, project.data.clusterConfigurations, workload.computeCluster,
        );
        const workloadReq: PcsSizingEngineRequest.Workload = {
          workloadId: workload.id!,
          ...(isDhci ? { deploymentMode: 'D_HCI' } : {}),
          cluster: {
            layout: computeConfig?.clusterType,
            // dHCI (vSAN Max) only: BE-added field carrying Storage's own layout alongside Compute's
            // `layout`, since the two can now diverge (Storage independently stretched).
            ...(isDhci ? { storageLayout: storageMarginConfig?.clusterType } : {}),
            // dHCI only: BE requires the flag on every request (true OR false), never omitted - it is the
            // same value that decides the SON chassis' networkSpeedEW/NS vs networkSpeed split below.
            ...(isDhci ? { trafficSeparation: workload.storageClusterConfiguration?.networkTrafficSeparation === true } : {}),
            vsanArchitecture: 'VSAN' + (workload.storageConfiguration.vsanArchitecture == 'OSA' ? '' : ' ' + workload.storageConfiguration.vsanArchitecture),
            profileMode: workload.storageConfiguration.vsanProfileMode,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any,
          features: {
            swapEnabled: storageMarginConfig?.accountForSwapSpace ?? false,
            // sizing-engine rejects vSAN Operations Reserve / Host Rebuild Reserve for stretched clusters (code 110013).
            hostRebuild: isStorageStretched ? false : (storageMarginConfig?.hostRebuildReserve ?? false),
          },
          margin: {
            cpu: {
              hostFailure: computeConfig?.cpuHeadroomHostFailureScenario,
              steadyState: computeConfig?.cpuHeadroomSteadyState,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
            // Model gap (not fixed): Margin.growthFactor is declared as NumValueUnit ({value, unit}),
            // but the payload actually sent uses `units` (plural), matching the sibling NumValueUnits
            // shape used everywhere else in this file. Preserved via `as unknown as`.
            growthFactor: {
              value: computeConfig?.growthFactor,
              units: '%',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
            ...(isDhci
              ? {
                  storageGrowthFactor: {
                    value: workload.storageClusterConfiguration?.growthFactor ?? 0,
                    units: '%',
                  },
                }
              : {}),
            operationsReserve: isStorageStretched ? false : (storageMarginConfig?.operationsReserve ?? false),
          },
          chassis: isDhci
            ? buildDhciChassis(workload, domain, project.data.clusterConfigurations)
            : effectiveHardwareConfiguration.hardwareConfigMode === 'no-preference'
              ? [buildIdentityOnlyChassis(workload.computeCluster?.name, 'HCI', undefined, workload.computeCluster?.id)]
              : [stampHciChassisIdentity(buildChassisFromHardwareConfig(effectiveHardwareConfiguration, workload.storageConfiguration.vsanArchitecture, undefined, workload.computeCluster?.id), workload.computeCluster?.name)],
          hardwarePreference: isDhci
            ? resolveDhciHardwarePreference(workload, domain, project.data.clusterConfigurations)
            : resolveHardwarePreference(effectiveHardwareConfiguration.hardwareConfigMode),
          ...(isDhci
            ? { storageHardwarePreference: resolveDhciStorageHardwarePreference(workload, domain, project.data.clusterConfigurations) }
            : {}),
          workloadData: {
            type: workload.workload.workload,
            name: workload.workload.workloadName,
            subtype: workload.workload.workloadType,
            vmProfile: buildVMProfilePayload(workload, project.metadata.version),
            ioProfile: buildIOProfilePayload(workload.workloadDetails),
            resourceUtilizationPlans: [
              {
                avgMemoryConsumption: {
                  value: workload.advancedSettings.resourceUtilizationPlan.avgMemoryConsumption,
                  units: '%',
                },
                cpu: {
                  avgConsumption: {
                    value: workload.advancedSettings.resourceUtilizationPlan.avgConsumption,
                    units: '%',
                  },
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any,
            ],
          },
          vsanOverheads: {
            coresConsumed: {
              value: workload.advancedSettings.vSANOverheads.coresConsumed,
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any,
        };
        if (workload.advancedSettings.vSANOverheads.cacheWriteIOAmplifier) {
          workloadReq.vsanOverheads.cacheWriteIOAmplifier = {
            value: workload.advancedSettings.vSANOverheads.cacheWriteIOAmplifier,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any;
        }
        if (workload.advancedSettings.vSANOverheads.maxBandwidthPerDisk) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (workloadReq.vsanOverheads as any).maxBandwidthPerDisk = {
            value: workload.advancedSettings.vSANOverheads.maxBandwidthPerDisk,
          };
        }
        if (workload.advancedSettings.vSANOverheads.capacityWriteIOAmplifier) {
          workloadReq.vsanOverheads.capacityWriteIOAmplifier = {
            value: workload.advancedSettings.vSANOverheads.capacityWriteIOAmplifier,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any;
        }
        if (workload.advancedSettings.vSANOverheads.maxIOpsPerDisk) {
          workloadReq.vsanOverheads.maxIOpsPerDisk = {
            value: workload.advancedSettings.vSANOverheads.maxIOpsPerDisk,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any;
        }
        workloads.push(workloadReq);
      }
      // Domain Request Object
      // Model gap (not fixed): PcsSizingEngineRequest.Requirement doesn't declare
      // workloadDomainType, but the WLD payload (unlike the MGMT one) always sends it.
      const domainReq: RequirementWithWorkloadDomainType = {
        requirementsId: project.data.workloadDomains[dIndex].id as unknown as string,
        requirementsName: project.data.workloadDomains[dIndex].domainName,
        domainType: 'WORKLOAD',
        workloadDomainType: project.data.workloadDomains[dIndex].domainType,
        productName: project.metadata.product,
        version: project.metadata.version,
        workloads: workloads,
        components: buildComponentsPayload(domain, context.wldComponents),
      };
      domainRequirements.push(domainReq);
    }
    designRequest = {
      design: {
        id: project.projectId,
        requirements: domainRequirements,
      },
    };
  }
  return designRequest;
}

/**
 * Ported as-is from PCSSizingEngineRequestAdapterService.getManagementDomainRecommendationRequest
 * (pcs-sizing-engine-request-adapter.service.ts:195-266).
 */
export function getManagementDomainRecommendationRequest(
  project: VCPM.Project,
  context: SizingRequestContext,
): PcsSizingEngineRequest.DesignRequest | null {
  let designRequest: PcsSizingEngineRequest.DesignRequest | null = null;
  if (project.data.workloadDomains && Array.isArray(project.data.workloadDomains) && project.data.workloadDomains.length > 0) {
    const domainRequirements: PcsSizingEngineRequest.Requirement[] = [];
    const domain = project.data.managementDomain;

    if (domain) {
      // Configuration
      const workload = project.data.managementDomain?.configuration;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let workloadReq: any = {};
      if (workload) {
        // Use mgmt domain's own clusterType if set, otherwise fall back to workload domain check
        const mgmtClusterType = workload.clusterConfiguration?.clusterType;
        const layout = mgmtClusterType || (hasStretchedClusterInWorkloadDomains(project) ? 'stretched' : 'standard');
        const isStretchedLayout = layout === 'stretched';
        workloadReq = {
          workloadId: 'MGMT',
          cluster: {
            layout: layout,
            vsanArchitecture: 'VSAN' + (workload.storageConfiguration.vsanArchitecture == 'OSA' ? '' : ' ' + workload.storageConfiguration.vsanArchitecture),
            profileMode: workload.storageConfiguration.vsanProfileMode,
          },
          features: {
            swapEnabled: workload.clusterConfiguration.accountForSwapSpace,
            // sizing-engine rejects vSAN Operations Reserve / Host Rebuild Reserve for stretched clusters (code 110013).
            hostRebuild: isStretchedLayout ? false : workload.clusterConfiguration.hostRebuildReserve,
          },
          margin: {
            cpu: {
              hostFailure: workload.clusterConfiguration.cpuHeadroomHostFailureScenario,
              steadyState: workload.clusterConfiguration.cpuHeadroomSteadyState,
            },
            growthFactor: {
              value: workload.clusterConfiguration.growthFactor,
              units: '%',
            },
            operationsReserve: isStretchedLayout ? false : workload.clusterConfiguration.operationsReserve,
          },
          chassis:
            workload.hardwareConfiguration.hardwareConfigMode === 'no-preference'
              ? [buildIdentityOnlyChassis('Management Domain', 'HCI')]
              : [stampHciChassisIdentity(buildHardwareConfigurationPayload(workload), 'Management Domain')],
          hardwarePreference: resolveHardwarePreference(workload.hardwareConfiguration.hardwareConfigMode),
          workloadData: {
            type: 'GPW',
            subtype: 'GVM',
            vmProfile: { raid: workload.clusterConfiguration.faultToleranceMethod, hostFailures: workload.clusterConfiguration.hostFailuresToTolerate },
          },
        };
        if ('autoMode' in workload.clusterConfiguration) {
          workloadReq['workloadData']['vmProfile']['autoMode'] = workload.clusterConfiguration.autoMode;
        }
        if (workload.clusterConfiguration.vcpuPerCore != null) {
          workloadReq['workloadData']['vmProfile']['vcpu'] = { perCore: workload.clusterConfiguration.vcpuPerCore };
        }
        if (workload.clusterConfiguration.vMemPerRam != null) {
          workloadReq['workloadData']['vmProfile']['vmem'] = { perRam: workload.clusterConfiguration.vMemPerRam };
        }
      }
      // Domain Request Object
      const domainReq: PcsSizingEngineRequest.Requirement = {
        requirementsId: AppConstants.MANAGEMENT_DOMAIN_REQUIREMENT_ID,
        requirementsName: 'Management Domain',
        domainType: 'MANAGEMENT',
        productName: project.metadata.product,
        version: project.metadata.version,
        workloads: [workloadReq],
        components: buildComponentsForMgmtPayload(domain, project, context),
      };
      domainRequirements.push(domainReq);
    }
    designRequest = {
      design: {
        id: project.projectId,
        requirements: domainRequirements,
      },
    };
  }
  return designRequest;
}

/**
 * Ported as-is from PCSSizingEngineRequestAdapterService.buildComponentsForMgmtPayload
 * (pcs-sizing-engine-request-adapter.service.ts:268-502).
 *
 * Gap H: `mgmtCompSuggestions` and `wldDomains` were read via separate
 * `this.store.selectSnapshot(ProjectDetailsState.getCurrentProject)` calls in the original service -
 * synchronous NGXS snapshots taken within the same call, always equal to the `project` argument
 * already passed into getManagementDomainRecommendationRequest. Threaded through `project` here
 * instead of a redundant second store read.
 */
export function buildComponentsForMgmtPayload(
  domain: VCPM.ManagementDomain,
  project: VCPM.Project,
  context: SizingRequestContext,
): PcsSizingEngineRequest.Component[] {
  let componentsToPayload: PcsSizingEngineRequest.Component[] = [];
  //user selection into a seperate simple object from mgmt domain components
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  domain.selectedComponents.swComponents.filter((swc: any) => swc.id != 0).forEach(
    (comp: VcfDomain.SoftwareComponentFormValue) => {
      // Skip AON, VCF Operations sub-components, and VRNI sub-components (they are handled separately)
      const skipComponents = ['AON', 'VCF_OPERATIONS', 'FLEET_MANAGEMENT', 'VCF_OPERATIONS_COLLECTOR', 'VRNI', 'VRNI_COLLECTOR'];
      if (!skipComponents.includes(comp.productName)) {
        componentsToPayload.push({
          id: comp.id,
          productName: comp.productName,
          enabled: comp.productSelected,
          brickSize: (comp.sizing as string).toUpperCase(),
          domainType: 'MANAGEMENT',
        });
      }
    },
  );
  // for AON components
  const mgmtComponents: DataProvider.Component[] = context.mgmtDomainComponents;
  const aonComponentsToPayload: PcsSizingEngineRequest.Component[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const aonCompSelection: any = domain.selectedComponents.swComponents.find((swc: VcfDomain.SoftwareComponentFormValue) => (swc.productName)?.toLocaleLowerCase() === ('AON').toLocaleLowerCase() && swc.productSelected == true);
  if (aonCompSelection && aonCompSelection.productSelected) {
    mgmtComponents.forEach((comp: DataProvider.Component) => {
      if ((comp.name).toLocaleLowerCase() === ('AON-COLLECTOR').toLocaleLowerCase()) {
        aonComponentsToPayload.push({
          id: comp.id,
          productName: comp.name,
          enabled: true,
          brickSize: aonCompSelection.aonCollectorSize,
          domainType: 'MANAGEMENT',
        });
      } else if ((comp.name).toLocaleLowerCase() === ('AON-PLATFORM').toLocaleLowerCase()) {
        aonComponentsToPayload.push({
          id: comp.id,
          productName: comp.name,
          enabled: true,
          brickSize: aonCompSelection.aonPlatformSize,
          domainType: 'MANAGEMENT',
        });
      }
    });
  }
  componentsToPayload = componentsToPayload.concat(aonComponentsToPayload);
  //remove AON from componentsToPayload
  componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) => (comp.productName)?.toLocaleLowerCase() !== ('AON').toLocaleLowerCase());

  // for VCF Operations composite components
  const vcfOpsComponentsToPayload: PcsSizingEngineRequest.Component[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const vcfOpsCompositeSelection: any = domain.selectedComponents.swComponents.find((swc: VcfDomain.SoftwareComponentFormValue) => (swc.productName)?.toLocaleLowerCase() === ('VCF_OPERATIONS_COMPOSITE').toLocaleLowerCase() && swc.productSelected == true);
  if (vcfOpsCompositeSelection && vcfOpsCompositeSelection.productSelected) {
    // Add VCF_OPERATIONS, FLEET_MANAGEMENT, and VCF_OPERATIONS_COLLECTOR components
    mgmtComponents.forEach((comp: DataProvider.Component) => {
      if ((comp.name).toLocaleLowerCase() === ('VCF_OPERATIONS').toLocaleLowerCase()) {
        vcfOpsComponentsToPayload.push({
          id: comp.id,
          productName: comp.name,
          enabled: true,
          brickSize: (vcfOpsCompositeSelection.vcfOperationsSizing)?.toUpperCase(),
          domainType: 'MANAGEMENT',
        });
      } else if ((comp.name).toLocaleLowerCase() === ('FLEET_MANAGEMENT').toLocaleLowerCase()) {
        vcfOpsComponentsToPayload.push({
          id: comp.id,
          productName: comp.name,
          enabled: true,
          brickSize: (vcfOpsCompositeSelection.fleetManagementSizing)?.toUpperCase(),
          domainType: 'MANAGEMENT',
        });
      } else if ((comp.name).toLocaleLowerCase() === ('VCF_OPERATIONS_COLLECTOR').toLocaleLowerCase()) {
        vcfOpsComponentsToPayload.push({
          id: comp.id,
          productName: comp.name,
          enabled: true,
          brickSize: (vcfOpsCompositeSelection.vcfOperationsCollectorSizing)?.toUpperCase(),
          domainType: 'MANAGEMENT',
        });
      }
    });
  }
  componentsToPayload = componentsToPayload.concat(vcfOpsComponentsToPayload);
  //remove VCF Operations composite from componentsToPayload
  componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) => (comp.productName)?.toLocaleLowerCase() !== ('VCF_OPERATIONS_COMPOSITE').toLocaleLowerCase());

  // for VRNI composite components
  const vrniComponentsToPayload: PcsSizingEngineRequest.Component[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const vrniCompositeSelection: any = domain.selectedComponents.swComponents.find((swc: VcfDomain.SoftwareComponentFormValue) => (swc.productName)?.toLocaleLowerCase() === ('VRNI_COMPOSITE').toLocaleLowerCase() && swc.productSelected == true);
  if (vrniCompositeSelection && vrniCompositeSelection.productSelected) {
    // Add VRNI and VRNI_COLLECTOR components
    mgmtComponents.forEach((comp: DataProvider.Component) => {
      if ((comp.name).toLocaleLowerCase() === ('VRNI').toLocaleLowerCase()) {
        vrniComponentsToPayload.push({
          id: comp.id,
          productName: comp.name,
          enabled: true,
          brickSize: (vrniCompositeSelection.vrniSizing)?.toUpperCase(),
          domainType: 'MANAGEMENT',
        });
      } else if ((comp.name).toLocaleLowerCase() === ('VRNI_COLLECTOR').toLocaleLowerCase()) {
        vrniComponentsToPayload.push({
          id: comp.id,
          productName: comp.name,
          enabled: true,
          brickSize: (vrniCompositeSelection.vrniCollectorSizing)?.toUpperCase(),
          domainType: 'MANAGEMENT',
        });
      }
    });
  }
  componentsToPayload = componentsToPayload.concat(vrniComponentsToPayload);
  //remove VRNI composite from componentsToPayload
  componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) => (comp.productName)?.toLocaleLowerCase() !== ('VRNI_COMPOSITE').toLocaleLowerCase());

  //mgmt domain suggestions updated with user selection
  // Deep clone to avoid mutating the original state object
  const originalMgmtCompSuggestions = project?.data.output?.recommendation?.workloadDomainsRecommendation?.result.mgmtComponentsSuggestions;
  const mgmtCompSuggestions: PcsSizingEngineResponse.SoftwareComponent[] | undefined = originalMgmtCompSuggestions
    ? JSON.parse(JSON.stringify(originalMgmtCompSuggestions))
    : undefined;

  // Check if mgmtCompSuggestions has any management domain components
  const hasMgmtComponents = mgmtCompSuggestions && mgmtCompSuggestions.length > 0 &&
    mgmtCompSuggestions.some(comp => comp.domainType === 'MANAGEMENT');

  if (hasMgmtComponents) {
    // Use backend suggestions and merge with user selections
    const componentsToPayloadMap = new Map(componentsToPayload.map((comp) => [comp.id, comp]));

    // Check if VCF Operations Composite is disabled to mark sub-components as disabled
    const isVcfOpsCompositeDisabled = !vcfOpsCompositeSelection || !vcfOpsCompositeSelection.productSelected;

    // Check if VRNI Composite is disabled to mark sub-components as disabled
    const isVrniCompositeDisabled = !vrniCompositeSelection || !vrniCompositeSelection.productSelected;

    // Build a lookup of user's workload domain component selections so that
    // workload-placed entries in mgmtCompSuggestions use the user's chosen
    // sizes instead of the engine's recommendation.
    const wldUserSelectionsMap = new Map<string, VcfDomain.SoftwareComponentFormValue>();
    const wldDomains = project?.data?.workloadDomains;
    wldDomains?.forEach((wld: VCPM.WorkloadDomain) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (wld.selectedComponents?.swComponents as any)?.forEach((swc: VcfDomain.SoftwareComponentFormValue) => {
        const key = `${wld.id}_${swc.productName}`;
        wldUserSelectionsMap.set(key, swc);
      });
    });

    mgmtCompSuggestions?.forEach((comp: PcsSizingEngineResponse.SoftwareComponent) => {
      // Include all components with placementType: 'MANAGEMENT' regardless of domainType
      // This includes workload domain components that should be placed in management domain

      // Only override management domain components, not workload domain components placed in management
      if (componentsToPayloadMap.has(comp.id) && comp.domainType == 'MANAGEMENT') {
        comp.enabled = componentsToPayloadMap.get(comp.id)?.enabled;
        comp.brickSize = componentsToPayloadMap.get(comp.id)?.brickSize;
      }

      // If VCF Operations Composite is disabled, disable the three sub-components
      if (isVcfOpsCompositeDisabled) {
        const vcfOpsSubComponents = ['VCF_OPERATIONS', 'FLEET_MANAGEMENT', 'VCF_OPERATIONS_COLLECTOR'];
        if (vcfOpsSubComponents.includes(comp.productName)) {
          comp.enabled = false;
        }
      }

      // If VRNI Composite is disabled, disable the two sub-components
      if (isVrniCompositeDisabled) {
        const vrniSubComponents = ['VRNI', 'VRNI_COLLECTOR'];
        if (vrniSubComponents.includes(comp.productName)) {
          comp.enabled = false;
        }
      }
    });
    //creating mgmt components payload as required by BE service
    componentsToPayload = [];
    mgmtCompSuggestions?.filter((comp: PcsSizingEngineResponse.SoftwareComponent) => {
      // Include all components with placementType: 'MANAGEMENT' regardless of domainType
      // This ensures workload domain components with management placement are included
      return comp.id != 0 && (comp.placementType)?.toLowerCase() === 'management';
    }).forEach(
      (comp: PcsSizingEngineResponse.SoftwareComponent) => {
        componentsToPayload.push({
          id: comp.id,
          productName: comp.productName,
          enabled: comp.enabled,
          brickSize: (comp.brickSize)?.toUpperCase(),
          domainType: comp.domainType,
          placementType: comp.placementType,
          workloadDomainType: comp.workloadDomainType ?? WORKLOAD_DOMAIN_TYPE.None,
          requirementsId: comp.requirementsId,
        });
      },
    );
  }
  // else: If no management components in suggestions, keep componentsToPayload from user selections (lines 272-349)
  const ariaSuiteSelection: VcfDomain.SoftwareComponentFormValue | undefined = domain.selectedComponents.swComponents
    .find((swc: VcfDomain.SoftwareComponentFormValue) => (swc.productName)?.toLocaleLowerCase() === ('ARIA-SUITE').toLocaleLowerCase());
  if (ariaSuiteSelection && ariaSuiteSelection.productSelected) {
    // Remove subComponents of Aria Suite from the request payload for sizing engine
    const subComponentIds: string[] = getSubcomponentIds('ARIA-SUITE', context.managementDomainInputConfig);
    if (subComponentIds && subComponentIds.length > 0) {
      componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) => !subComponentIds.includes(String(comp.productName)));
    }
  }

  if (aonCompSelection) {
    componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) => (comp.productName)?.toLocaleLowerCase() !== ('AON').toLocaleLowerCase());
  } else {
    //remove AON, AON-PLATFORM and AON-COLLECTOR from componentsToPayload
    componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) => (comp.productName)?.toLocaleLowerCase() !== ('AON-PLATFORM').toLocaleLowerCase());
    componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) => (comp.productName)?.toLocaleLowerCase() !== ('AON-COLLECTOR').toLocaleLowerCase());
    componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) => (comp.productName)?.toLocaleLowerCase() !== ('AON').toLocaleLowerCase());
  }

  if (vcfOpsCompositeSelection && vcfOpsCompositeSelection.productSelected) {
    // Remove VCF Operations composite from payload, but keep the three sub-components
    componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) => (comp.productName)?.toLocaleLowerCase() !== ('VCF_OPERATIONS_COMPOSITE').toLocaleLowerCase());
  } else {
    // When composite is disabled, remove the composite from payload (the three sub-components come from backend suggestions)
    componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) =>
      (comp.productName)?.toLocaleLowerCase() !== ('VCF_OPERATIONS_COMPOSITE').toLocaleLowerCase(),
    );
  }

  if (vrniCompositeSelection && vrniCompositeSelection.productSelected) {
    // Remove VRNI composite from payload, but keep the two sub-components
    componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) => (comp.productName)?.toLocaleLowerCase() !== ('VRNI_COMPOSITE').toLocaleLowerCase());
  } else {
    // When composite is disabled, remove the composite from payload (the two sub-components come from backend suggestions)
    componentsToPayload = componentsToPayload.filter((comp: PcsSizingEngineRequest.Component) =>
      (comp.productName)?.toLocaleLowerCase() !== ('VRNI_COMPOSITE').toLocaleLowerCase(),
    );
  }

  return componentsToPayload;
}

/**
 * Ported as-is from PCSSizingEngineRequestAdapterService.buildComponentsPayload
 * (pcs-sizing-engine-request-adapter.service.ts:504-591).
 */
export function buildComponentsPayload(domain: VCPM.WorkloadDomain, wldComponents: DataProvider.Component[]): PcsSizingEngineRequest.Component[] {
  const componentsToPayload: PcsSizingEngineRequest.Component[] = [];
  const dataProviderComponents: DataProvider.Component[] = wldComponents;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isHCXComponentSelected: boolean = domain.selectedComponents.swComponents.some((comp: any) => (comp.productName).toLocaleLowerCase() == ('HCX').toLocaleLowerCase() && comp.productSelected == true);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hcxOptionalComponent = domain.selectedComponents.swComponents.find((comp: any) => (comp.productName).toLocaleLowerCase() == ('HCX').toLocaleLowerCase() && comp.productSelected == true);
  if (isHCXComponentSelected) {
    const mandatoryHCXComponents: DataProvider.Component[] = dataProviderComponents.filter((comp: DataProvider.Component) => (comp.name).toLocaleLowerCase() === ('HCX-IX').toLocaleLowerCase() || (comp.name).toLocaleLowerCase() === ('HCX-MANAGER').toLocaleLowerCase() || (comp.name).toLocaleLowerCase() === ('HCX-NE').toLocaleLowerCase());
    mandatoryHCXComponents.forEach((comp: DataProvider.Component) => {
      componentsToPayload.push({
        id: comp.id,
        productName: comp.name,
        enabled: true,
        brickSize: comp.defaultSizing,
        domainType: 'WORKLOAD',
        workloadDomainType: domain.domainType,
      });
    });
    //check if optional components selected
    if (hcxOptionalComponent.migrateNonVsphere && (hcxOptionalComponent.migrateNonVsphere).toLocaleLowerCase() === 'yes') {
      dataProviderComponents.filter((comp: DataProvider.Component) => (comp.name).toLocaleLowerCase() === ('HCX-SRG').toLocaleLowerCase()).forEach((comp: DataProvider.Component) => {
        componentsToPayload.push({
          id: comp.id,
          productName: comp.name,
          enabled: true,
          brickSize: comp.defaultSizing,
          domainType: 'WORKLOAD',
          workloadDomainType: domain.domainType,
          parameters: [
            {
              key: 'migrateNonVsphere',
              value: hcxOptionalComponent.migrateNonVsphere,
            },
          ],
        });
      });
    }
    if (hcxOptionalComponent.migrateOverInternet && (hcxOptionalComponent.migrateOverInternet).toLocaleLowerCase() === 'yes') {
      dataProviderComponents.filter((comp: DataProvider.Component) => (comp.name).toLocaleLowerCase() === ('HCX-WAN').toLocaleLowerCase()).forEach((comp: DataProvider.Component) => {
        componentsToPayload.push({
          id: comp.id,
          productName: comp.name,
          enabled: true,
          brickSize: comp.defaultSizing,
          domainType: 'WORKLOAD',
          workloadDomainType: domain.domainType,
          parameters: [
            {
              key: 'migrateOverInternet',
              value: hcxOptionalComponent.migrateOverInternet,
            },
          ],
        });
      });
    }
  }
  domain.selectedComponents.swComponents.filter((swc: VcfDomain.SoftwareComponentFormValue) => swc.id != 0 && (swc.productName).toLocaleLowerCase() !== ('NSX-MANAGER').toLocaleLowerCase() && (swc.productName).toLocaleLowerCase() !== ('HCX').toLocaleLowerCase() && swc.productSelected === true).forEach(
    (comp: VcfDomain.SoftwareComponentFormValue) => {
      componentsToPayload.push({
        id: comp.id,
        productName: comp.productName,
        enabled: comp.productSelected,
        brickSize: comp.sizing,
        domainType: 'WORKLOAD',
        workloadDomainType: domain.domainType,
      });
    },
  );
  domain.selectedComponents.swComponents.filter((swc: VcfDomain.SoftwareComponentFormValue) => swc.id != 0 && (swc.productName).toLocaleLowerCase() === ('NSX-MANAGER').toLocaleLowerCase() && swc.productSelected === true).forEach(
    (comp: VcfDomain.SoftwareComponentFormValue) => {
      componentsToPayload.push({
        id: comp.id,
        productName: comp.productName,
        enabled: comp.productSelected,
        brickSize: comp.sizing,
        domainType: 'WORKLOAD',
        workloadDomainType: domain.domainType,
        parameters: [
          {
            key: 'isNsxManagerShared',
            value: comp.deploymentType,
          },
        ],
      });
    },
  );
  return componentsToPayload;
}

/**
 * NO_PREFERENCE when the workload has no hardware preference at all; HARDWARE_FIRST otherwise
 * (readynode-based, cpu-based, or use-existing-cluster).
 */
export function resolveHardwarePreference(hardwareConfigMode?: string): HARDWARE_PREFERENCE {
  return hardwareConfigMode === 'no-preference' ? HARDWARE_PREFERENCE.NoPreference : HARDWARE_PREFERENCE.HardwareFirst;
}

/**
 * 'use-existing-cluster' is a wrapper mode, never a terminal one: a workload/side referencing a
 * shared cluster entity (VCPM.HCICluster/ComputeCluster/StorageCluster) deliberately keeps its own
 * local-mirror hardwareConfigMode pinned to this sentinel (see syncReferencedHardwareConfiguration
 * in sizing-recalculation.adapter.ts, and ClusterHardwareConfigComponent.onPicked()) so the wizard
 * radio doesn't silently change on reopen - the REAL mode (cpu-based/readynode-based/esxi/
 * no-preference) only ever lives on the referenced cluster entity's own canonical
 * hardwareConfiguration. Chassis-building and hardware-preference resolution need the real mode, so
 * both must resolve through this one hop (workload -> ClusterReference.id -> cluster entity) before
 * branching on hardwareConfigMode - mirrors ClusterHardwareConfigComponent.getDisplayMode()'s UI-only
 * equivalent. Returns `hw` unchanged for any other mode, or if the reference can't be resolved (an
 * orphaned reference falls back to the mirror as-is rather than throwing).
 */
export function resolveEffectiveHardwareConfiguration(
  hw: VCPM.HardwareConfiguration,
  domain: VCPM.WorkloadDomain,
  storageClusters: VCPM.StorageCluster[] | undefined,
  clusterReference: VCPM.ClusterReference | null | undefined,
): VCPM.HardwareConfiguration {
  if (hw.hardwareConfigMode !== 'use-existing-cluster' || !clusterReference) {
    return hw;
  }
  const cluster = domain.clusterConfigurations?.find((c) => c.id === clusterReference.id)
    ?? storageClusters?.find((c) => c.id === clusterReference.id);
  return cluster?.hardwareConfiguration ?? hw;
}

/**
 * Ported as-is from PCSSizingEngineRequestAdapterService.buildHardwareConfigurationPayload
 * (pcs-sizing-engine-request-adapter.service.ts:593-674). Delegates to buildChassisFromHardwareConfig
 * so the HCI path and the dHCI compute/storage chassis builder (buildDhciChassis) share one
 * implementation - behavior here is unchanged from before that extraction.
 */
export function buildHardwareConfigurationPayload(workload: VCPM.Workload | VCPM.ManagementDomainConfiguration): PcsSizingEngineRequest.Chassis {
  return buildChassisFromHardwareConfig(workload.hardwareConfiguration, workload.storageConfiguration.vsanArchitecture);
}

/**
 * Stamps identity onto a single-cluster (non-dHCI) chassis: the HCI workload-domain path sources
 * `clusterName` from `workload.computeCluster?.name` (the same reference field dHCI's
 * compute/storage sides use), the management-domain path passes the fixed literal
 * `'Management Domain'` since that domain has no reusable-cluster-entity concept. Mirrors
 * the clusterType stamping buildDhciChassis already does for CON/SON chassis.
 */
function stampHciChassisIdentity(chassis: PcsSizingEngineRequest.Chassis, clusterName: string | undefined): PcsSizingEngineRequest.Chassis {
  chassis.clusterName = clusterName;
  chassis.clusterType = 'HCI';
  return chassis;
}

/**
 * Identity-only chassis for the 'no-preference' case: the sizing engine picks hardware itself, so
 * there's no hardware detail to send (components is intentionally omitted), but the
 * multi-workload-in-a-cluster feature still needs clusterName/clusterType (and owner, for SON)
 * echoed back on the response to correlate this workload's cluster with others sharing the same
 * cluster - mirrors stampHciChassisIdentity/buildDhciChassis's own stamping, just without
 * buildChassisFromHardwareConfig's hardware fields.
 *
 * `clusterId`, when the workload already owns/references a persisted cluster entity
 * (computeCluster/storageCluster.id), is threaded straight into chassis.id instead of the
 * 'no-preference' placeholder - see buildChassisFromHardwareConfig's own doc comment for why.
 */
function buildIdentityOnlyChassis(
  clusterName: string | undefined,
  clusterType: 'CON' | 'SON' | 'HCI',
  owner?: boolean,
  clusterId?: string,
): PcsSizingEngineRequest.Chassis {
  return {
    id: clusterId ?? 'no-preference',
    clusterName,
    clusterType,
    ...(owner !== undefined ? { owner } : {}),
  };
}

/**
 * dHCI (vSAN Max): builds the workload's chassis[] as up to two entries - one CON (compute) chassis
 * from computeClusterHardwareConfiguration, one SON (storage) chassis from
 * storageClusterHardwareConfiguration - mirroring the two sample BE payloads (one dHCI cluster split
 * across a compute-only and a storage-only-node chassis). A side whose hardwareConfigMode is
 * 'no-preference' still gets a chassis entry (via buildIdentityOnlyChassis) - no hardware-derived
 * fields, but clusterName/clusterType/owner are still stamped so multi-workload-in-a-cluster
 * grouping keeps working for that side, matching the single-chassis no-preference behavior below.
 *
 * `owner` is SON-chassis-only (storage clusters can be shared across workload domains; compute
 * clusters cannot) and is always present as a boolean: `true` on the chassis belonging to the
 * domain that owns the storage cluster (StorageCluster.workloadDomainId), `false` on every other
 * domain referencing it - regardless of how many domains share it, including a storage cluster
 * used solely within its own domain (which is trivially its own owner).
 */
export function buildDhciChassis(
  workload: VCPM.Workload,
  domain: VCPM.WorkloadDomain,
  storageClusters: VCPM.StorageCluster[] | undefined,
): PcsSizingEngineRequest.Chassis[] {
  const chassis: PcsSizingEngineRequest.Chassis[] = [];
  const vsanArchitecture = workload.storageConfiguration.vsanArchitecture;

  const computeHw = workload.computeClusterHardwareConfiguration;
  if (computeHw) {
    const effectiveComputeHw = resolveEffectiveHardwareConfiguration(computeHw, domain, storageClusters, workload.computeCluster);
    if (effectiveComputeHw.hardwareConfigMode !== 'no-preference') {
      const computeChassis = buildChassisFromHardwareConfig(effectiveComputeHw, vsanArchitecture, undefined, workload.computeCluster?.id);
      computeChassis.clusterName = workload.computeCluster?.name;
      computeChassis.clusterType = 'CON';
      // Drive fields are inapplicable to CON, including CPU and reused HCI hardware.
      computeChassis.components?.storage.available.forEach(storage => {
        storage.maxDriveSlots = 0;
        storage.capacityDriveSize.value = 0;
      });
      chassis.push(computeChassis);
    } else {
      chassis.push(buildIdentityOnlyChassis(workload.computeCluster?.name, 'CON', undefined, workload.computeCluster?.id));
    }
  }

  const storageHw = workload.storageClusterHardwareConfiguration;
  if (storageHw) {
    const storageClusterId = workload.storageCluster?.id;
    const storageCluster = storageClusters?.find((sc) => sc.id === storageClusterId);
    const owner = storageCluster?.workloadDomainId === domain.id;
    const effectiveStorageHw = resolveEffectiveHardwareConfiguration(storageHw, domain, storageClusters, workload.storageCluster);

    if (effectiveStorageHw.hardwareConfigMode !== 'no-preference') {
      const storageChassis = buildChassisFromHardwareConfig(
        effectiveStorageHw,
        vsanArchitecture,
        workload.storageClusterConfiguration?.networkTrafficSeparation,
        workload.storageCluster?.id,
      );
      storageChassis.clusterName = workload.storageCluster?.name;
      storageChassis.clusterType = 'SON';
      storageChassis.owner = owner;
      chassis.push(storageChassis);
    } else {
      const identityChassis = buildIdentityOnlyChassis(workload.storageCluster?.name, 'SON', owner, workload.storageCluster?.id);
      chassis.push(identityChassis);
    }
  }

  return chassis;
}

/**
 * dHCI (vSAN Max) counterpart to resolveHardwarePreference(): reflects the Compute side's own
 * effective hardwareConfigMode alone - NO_PREFERENCE when explicitly 'no-preference', HARDWARE_FIRST
 * otherwise (readynode-based, cpu-based, esxi, or use-existing-cluster resolved through to one of
 * those). No longer combined with the Storage side (that combination previously meant a no-preference
 * Compute side with a Storage side that had a preference incorrectly reported HARDWARE_FIRST here);
 * the Storage side drives its own independent field, storageHardwarePreference, via
 * resolveDhciStorageHardwarePreference() below.
 */
export function resolveDhciHardwarePreference(
  workload: VCPM.Workload,
  domain: VCPM.WorkloadDomain,
  storageClusters: VCPM.StorageCluster[] | undefined,
): HARDWARE_PREFERENCE {
  const effectiveComputeMode = workload.computeClusterHardwareConfiguration
    ? resolveEffectiveHardwareConfiguration(workload.computeClusterHardwareConfiguration, domain, storageClusters, workload.computeCluster).hardwareConfigMode
    : undefined;
  return resolveHardwarePreference(effectiveComputeMode);
}

/**
 * Storage-side counterpart to resolveDhciHardwarePreference(): unlike that function (which combines
 * both the CON and SON sides into one workload-level `hardwarePreference`), this reflects the
 * storage side's own effective mode alone (NO_PREFERENCE only when explicitly 'no-preference',
 * HARDWARE_FIRST for readynode-based, cpu-based, esxi, or use-existing-cluster resolved to one of
 * those - same rule as the Compute side). Stamped onto the workload-level `storageHardwarePreference`
 * field (sibling of `hardwarePreference`, not onto the SON chassis) so it stays in sync whenever CON
 * or SON is (re)customized at the output components - both recompute chassis/preferences via
 * buildDhciChassis/resolveDhciStorageHardwarePreference on every recalculation. Undefined when the
 * workload has no storage side at all (non-dHCI).
 */
export function resolveDhciStorageHardwarePreference(
  workload: VCPM.Workload,
  domain: VCPM.WorkloadDomain,
  storageClusters: VCPM.StorageCluster[] | undefined,
): 'HARDWARE_FIRST' | 'NO_PREFERENCE' | undefined {
  if (!workload.storageClusterHardwareConfiguration) {
    return undefined;
  }
  const effectiveStorageHw = resolveEffectiveHardwareConfiguration(
    workload.storageClusterHardwareConfiguration, domain, storageClusters, workload.storageCluster,
  );
  return resolveHardwarePreference(effectiveStorageHw.hardwareConfigMode);
}

/**
 * Core chassis builder shared by the HCI path (buildHardwareConfigurationPayload) and the dHCI
 * compute/storage path (buildDhciChassis). Extracted, not rewritten - identical to the body that
 * used to live directly in buildHardwareConfigurationPayload, just parameterized on
 * hardwareConfiguration/vsanArchitecture instead of reading them off a workload.
 *
 * `clusterId`, when the workload already owns/references a persisted cluster entity, is threaded
 * into chassis.id in place of the vendor-model placeholder - chassis.id is otherwise never read
 * downstream today, so this gives the output-side customization round trip a precedented,
 * id-based handle onto the cluster (mirrors Design.id/requirementsId/workloadId/Component.id,
 * which already round-trip request->response unchanged elsewhere in this adapter) at no cost.
 */
function buildChassisFromHardwareConfig(
  hw: VCPM.HardwareConfiguration,
  vsanArchitecture: string,
  networkTrafficSeparation?: boolean,
  clusterId?: string,
): PcsSizingEngineRequest.Chassis {
  const isReadyNodeBased = hw.hardwareConfigMode === 'readynode-based' && !!hw.readyNodeBasedConfig?.readyNodeSelection;
  const isEsxiBased = hw.hardwareConfigMode === 'esxi';

  const resolved = isReadyNodeBased
    ? resolveReadyNodeHardwareValues(hw)
    : isEsxiBased
      ? resolveEsxiHardwareValues(hw)
      : resolveCpuBasedHardwareValues(hw);

  const hardwareConfiguration: PcsSizingEngineRequest.Chassis = {
    id: clusterId ?? `${resolved.vendor}-${resolved.model}`,
    components: {
      cpu: {
        minCount: 2,
        available: [
          {
            name: resolved.cpuName,
            maxCount: resolved.sockets,
            cores: resolved.cores,
            speed: {
              value: resolved.clockSpeed,
              units: 'GHz',
            },
            sockets: resolved.sockets,
          },
        ],
      },
      memory: {
        available: [
          {
            name: 'DDR4',
            serverRam: {
              value: resolved.serverRam,
              units: 'GB',
            },
          },
        ],
      },
      storage: {
        available: [buildStorageConfiguration(hw, resolved)],
      },
    },
  };

  // Add 'part' object for plain CPU-based hardware only. ESXi server selection and ReadyNode
  // selection populate 'readynode' / 'server' respectively instead (per sizing-engine contract).
  if (!isReadyNodeBased && !isEsxiBased) {
    hardwareConfiguration.part = {
      vendor: resolved.vendor,
      generation: resolved.generation,
      series: resolved.series,
      category: resolved.category,
      model: resolved.model,
      clockSpeed: resolved.clockSpeed,
      sockets: resolved.sockets,
      cost: 0,
    };
  }

  // Add 'readynode' object for ReadyNode-based hardware
  if (isReadyNodeBased && hw.readyNodeBasedConfig?.readyNodeSelection) {
    const readyNode = hw.readyNodeBasedConfig.readyNodeSelection;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const readyNodePayload: any = {};

    readyNodePayload.systemId = readyNode.readyNodeId;
    readyNodePayload.model = readyNode.serverModel;
    readyNodePayload.vendor = readyNode.vendor;
    readyNodePayload.generation = readyNode.generation;
    readyNodePayload.profileName = hw.readyNodeBasedConfig?.recommendedProfileName || readyNode.profileName;

    hardwareConfiguration.readynode = readyNodePayload;
  }

  // Add 'server' object for ESXi-based hardware (CON clusters). CON chassis never carry
  // 'readynode' - the ESXi server selection is sent under 'server' per the sizing-engine contract.
  if (isEsxiBased && hw.esxiBasedConfig?.esxiServerSelection) {
    const esxiServer = hw.esxiBasedConfig.esxiServerSelection;
    const systemId = Number(esxiServer.serverId);
    const server: PcsSizingEngineRequest.Server = {
      model: esxiServer.serverModel,
      generation: esxiServer.generation,
      vendor: esxiServer.vendor,
      cpuModelSeries: esxiServer.cpuModelSeries,
    };
    if (esxiServer.serverId != null && esxiServer.serverId !== '' && Number.isFinite(systemId)) {
      server.systemId = systemId;
    }

    hardwareConfiguration.server = server;
  }

  if (vsanArchitecture.toLocaleLowerCase() === AppConstants.vSAN_ESA.toLocaleLowerCase()) {
    // components is always set above in this builder (unlike the identity-only No-Preference chassis).
    const cpuAvailable = hardwareConfiguration.components!.cpu.available[0];
    // Traffic separation (dHCI Storage/SON only): the converged networkSpeed field is unused in
    // favor of the split EW (backend)/NS (client-facing) pair - see CPUAvailable's own doc comment
    // and the API's networkSpeedNS/networkSpeedEW schema.
    if (networkTrafficSeparation) {
      cpuAvailable.networkSpeedEW = { value: resolved.networkSpeed, units: 'Gbps' };
      cpuAvailable.networkSpeedNS = { value: resolved.networkSpeedNS, units: 'Gbps' };
    } else {
      cpuAvailable.networkSpeed = { value: resolved.networkSpeed, units: 'Gbps' };
    }
  }

  return hardwareConfiguration;
}

interface ResolvedHardwareValues {
  vendor: string;
  model: string;
  generation: string;
  series: string;
  category: string;
  clockSpeed: number | string;
  cpuName: string;
  sockets: number;
  cores: number;
  serverRam: number;
  networkSpeed: number;
  networkSpeedNS: number;
  numberOfDrives: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  driveSize: any;
}

// Module-private, not exported - ported as-is from
// pcs-sizing-engine-request-adapter.service.ts:676-697.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolveReadyNodeHardwareValues(hw: any): ResolvedHardwareValues {
  const readyNode = hw.readyNodeBasedConfig?.readyNodeSelection;
  const common = hw.commonConfig;

  return {
    vendor: readyNode?.vendor || '',
    model: readyNode?.serverModel || '',
    generation: readyNode?.generation || '',
    series: readyNode?.profileName || '',
    category: 'ReadyNode',
    clockSpeed: Number(common?.clockSpeed) || Number(readyNode?.defaultCpuClockSpeed) || 0,
    cpuName: `${readyNode?.vendor} ${readyNode?.serverModel}`,
    sockets: Number(common?.sockets) || Number(readyNode?.defaultCpuSockets) || Number(readyNode?.cpuSockets) || 0,
    cores: Number(common?.cores) || Number(readyNode?.cores) || 0,
    serverRam: Number(common?.serverRam) || Number(readyNode?.defaultMemory) || Number(readyNode?.memory) || 0,
    networkSpeed: Number(common?.networkSpeed) || Number(readyNode?.networkSpeed) || 0,
    networkSpeedNS: Number(common?.networkSpeedNS) || 0,
    numberOfDrives: Number(common?.numberOfDrives) || Number(readyNode?.defaultDriveCount) || Number(readyNode?.maxDriveCount) || 0,
    driveSize:
      common?.driveSize === 'other' || common?.driveSize === 'Other'
        ? Number(common?.customDriveSize) || 0
        : Number(common?.driveSize) || Number(readyNode?.capacity) || 0,
  };
}

/**
 * CON-only counterpart to resolveReadyNodeHardwareValues/resolveCpuBasedHardwareValues, for
 * hardwareConfigMode: 'esxi'. ESXi customization deliberately uses its own field shapes
 * (esxiBasedConfig.esxiServerSelection / esxiCommonConfig) rather than commonConfig/cpuBasedConfig -
 * see HardwareConfiguration.esxiCommonConfig's own doc comment ("ESXi has no drive/network
 * concepts"). driveSize/numberOfDrives/networkSpeed are hardcoded to 0 (not left undefined) so the
 * resulting chassis is always JSON-serializable and satisfies the sizing-engine's required
 * capacityDriveSize.value field even when nothing has been customized yet.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolveEsxiHardwareValues(hw: any): ResolvedHardwareValues {
  const esxiServer = hw.esxiBasedConfig?.esxiServerSelection;
  const common = hw.esxiCommonConfig;

  return {
    vendor: esxiServer?.vendor || '',
    model: esxiServer?.serverModel || '',
    generation: esxiServer?.generation || '',
    series: esxiServer?.cpuModelSeries || '',
    category: 'ESXi Server',
    clockSpeed: Number(common?.clockSpeed) || Number(esxiServer?.maxClockSpeed) || 0,
    cpuName: `${esxiServer?.vendor || ''} ${esxiServer?.serverModel || ''}`.trim(),
    sockets: Number(common?.sockets) || Number(esxiServer?.maxCpuSockets) || 0,
    cores: Number(common?.coresPerSocket) || Number(esxiServer?.maxCoresPerSocket) || 0,
    serverRam: Number(common?.memory) || Number(esxiServer?.maxMemory) || 0,
    networkSpeed: 0,
    networkSpeedNS: 0,
    numberOfDrives: 0,
    driveSize: 0,
  };
}

// Module-private, not exported - ported as-is from
// pcs-sizing-engine-request-adapter.service.ts:699-728.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolveCpuBasedHardwareValues(hw: any): ResolvedHardwareValues {
  // Support both nested (Workload Domain) and flat (Management Domain) structures
  const cpuConfig = hw.cpuBasedConfig;
  const common = hw.commonConfig;

  // Determine driveSize - check both nested and flat structures
  const driveSizeFromNested = common?.driveSize === 'other' || common?.driveSize === 'Other' ? common?.customDriveSize : common?.driveSize;
  const driveSizeFromFlat = hw.driveSize === 'other' || hw.driveSize === 'Other' ? hw.customDriveSize : hw.driveSize;
  const driveSizeValue = driveSizeFromNested || driveSizeFromFlat;

  return {
    vendor: cpuConfig?.cpuVendor || hw.cpuVendor || '',
    model: cpuConfig?.cpuModel || hw.cpuModel || '',
    generation: cpuConfig?.cpuGeneration || hw.cpuGeneration || '',
    series: cpuConfig?.cpuSeries || hw.cpuSeries || '',
    category: cpuConfig?.cpuCategory || hw.cpuCategory || '',
    clockSpeed: (common?.clockSpeed || hw.clockSpeed || 0) as number | string,
    cpuName: cpuConfig?.cpuModel || hw.cpuModel || '',
    sockets: Number(common?.sockets || hw.sockets) || 0,
    cores: Number(common?.cores || hw.cores) || 0,
    serverRam: Number(common?.serverRam || hw.serverRam) || 0,
    networkSpeed: Number(common?.networkSpeed || hw.networkSpeed) || 0,
    networkSpeedNS: Number(common?.networkSpeedNS) || 0,
    numberOfDrives: Number(common?.numberOfDrives || hw.numberOfDrives) || 0,
    driveSize: driveSizeValue,
  };
}

/**
 * Builds storage configuration with conditional userDefinedDiskGroups field.
 * Only includes userDefinedDiskGroups when diskGroup is "User Defined DiskGroup".
 * Handles both flat structure (legacy) and nested commonConfig structure (new).
 *
 * Module-private, not exported - ported as-is from
 * pcs-sizing-engine-request-adapter.service.ts:49-82.
 */
export function buildStorageConfiguration(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hardwareConfiguration: any,
  resolved: ResolvedHardwareValues,
): PcsSizingEngineRequest.StorageAvailable {
  // Extract OSA-specific values from either root level or commonConfig
  const diskGroup = hardwareConfiguration.diskGroup || hardwareConfiguration.commonConfig?.diskGroup;
  const cacheTierMediaRating = hardwareConfiguration.cacheTierMediaRating || hardwareConfiguration.commonConfig?.cacheTierMediaRating;
  const userDefinedDiskGroups = hardwareConfiguration.userDefinedDiskGroups || hardwareConfiguration.commonConfig?.userDefinedDiskGroups;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const storageConfig: any = {
    name: 'SAS SSD',
    capacityDriveSize: {
      value: resolved.driveSize,
      units: 'TB',
    },
    maxDriveSlots: resolved.numberOfDrives,
  };

  // Only add OSA-specific fields if they exist
  if (diskGroup !== undefined) {
    storageConfig.diskGroup = {
      value: diskGroup,
    };
  }

  if (cacheTierMediaRating !== undefined) {
    storageConfig.cacheTierMediaRating = {
      value: convertCacheTierMediaRating(cacheTierMediaRating),
    };
  }

  if (diskGroup === 'User Defined DiskGroup') {
    storageConfig.userDefinedDiskGroups = userDefinedDiskGroups;
  }

  return storageConfig;
}

/**
 * Convert cache tier media rating string to numeric value.
 * Module-private, not exported - ported as-is from
 * pcs-sizing-engine-request-adapter.service.ts:30-42.
 */
export function convertCacheTierMediaRating(rating: string | number): number {
  if (typeof rating === 'number') {
    return rating;
  }

  const ratingMap: { [key: string]: number } = {
    Low: 1,
    Medium: 2,
    High: 3,
  };

  return ratingMap[rating] || 2; // Default to Medium (2) if unknown
}

/**
 * Ported as-is from PCSSizingEngineRequestAdapterService.buildVMProfilePayload
 * (pcs-sizing-engine-request-adapter.service.ts:730-844).
 *
 * The dedup rule (VersionUtils.isVsanEsaDedupSupported) is the sole enforcement point of the
 * dedupRatio/vcfVersion gate - see plan Section 1.9. Porting only relocates this guardrail; it
 * does not close the backend gap (sizing-engine performs no server-side version validation of its
 * own for dedupRatio). A backend ticket should be filed separately for that gap - not part of this
 * mechanical port.
 */
export function buildVMProfilePayload(workload: VCPM.Workload, vcfVersion?: string): PcsSizingEngineRequest.VMProfile {
  const vmProfile: PcsSizingEngineRequest.VMProfile = {
    compression: workload.workloadDetails.compression,
    hostFailures: parseInt(workload.workloadDetails.hostFailuresToTolerate),
    raid: workload.workloadDetails.faultToleranceMethod,
    iops: {
      perVm: workload.workloadDetails.iopsPerVM,
    },
    vcpu: {
      perCore: workload.workloadDetails.vcpuPerCore,
      perVm: workload.workloadDetails.vcpuPerVM,
    },
    vmem: {
      perVm: workload.workloadDetails.vmemPerVM,
    },
    vmsCount: workload.workloadDetails.totalCountOfVms,
  };

  if (workload.workloadDetails.compression) {
    vmProfile.compression = workload.workloadDetails.compression;
    vmProfile.dedupRatio = 1;
  } else if (
    workload.workloadDetails.dedup &&
    workload.storageConfiguration.storageType === 'VSAN' &&
    (workload.storageConfiguration.vsanArchitecture === 'OSA' ||
      (workload.storageConfiguration.vsanArchitecture === 'ESA' && vcfVersion && VersionUtils.isVsanEsaDedupSupported(vcfVersion)))
  ) {
    vmProfile.dedupRatio = workload.workloadDetails.dedup;
  } else {
    vmProfile.dedupRatio = 1;
  }
  switch (workload.workload.workloadType) {
    case 'GVM':
    case 'ORA':
    case 'SQL':
    case 'FCL':
      vmProfile.vmdk = {
        growthFactor: {
          value: 1.1,
          units: '%',
        },
        size: {
          value: workload.workloadDetails.storagePerVM,
          units: 'GB',
        },
      };
      break;
    case 'LCL':
      vmProfile.vmdk = {
        growthFactor: {
          value: workload.workloadDetails.expecVmdkGrowth ? workload.workloadDetails.expecVmdkGrowth : 0,
          units: '%',
        },
      };
      vmProfile.clone = {
        expectedGrowth: {
          value: workload.workloadDetails.expecCloneGrowth ? workload.workloadDetails.expecCloneGrowth : 0,
          units: '%',
        },
        instantClonePools: workload.workloadDetails.numOfInstClonePools ? workload.workloadDetails.numOfInstClonePools : 0,
        linkedPools: workload.workloadDetails.numOfLinkedClonePools ? workload.workloadDetails.numOfLinkedClonePools : 0,
      };
      vmProfile.masterVm = {
        size: {
          value: workload.workloadDetails.sizeOfMasterVM ? workload.workloadDetails.sizeOfMasterVM : 0,
          units: 'GB',
        },
        diskCount: workload.workloadDetails.numOfDisksInMasterVM ? workload.workloadDetails.numOfDisksInMasterVM : 0,
      };
      vmProfile.persistentDiskSize = {
        value: workload.workloadDetails.persistentDiskSize ? workload.workloadDetails.persistentDiskSize : 0,
        units: 'GB',
      };
      vmProfile.sddSize = {
        value: workload.workloadDetails.sysDisposableDiskSize ? workload.workloadDetails.sysDisposableDiskSize : 0,
        units: 'GB',
      };
      break;
    case 'ICL':
      vmProfile.masterVm = {
        size: {
          value: workload.workloadDetails.sizeOfMasterVM ? workload.workloadDetails.sizeOfMasterVM : 0,
          units: 'GB',
        },
        diskCount: 0,
      };
      vmProfile.vmdk = {
        growthFactor: {
          value: workload.workloadDetails.expecVmdkGrowth ? workload.workloadDetails.expecVmdkGrowth : 0,
          units: '%',
        },
      };
      vmProfile.clone = {
        expectedGrowth: {
          value: workload.workloadDetails.expecCloneGrowth ? workload.workloadDetails.expecCloneGrowth : 0,
          units: '%',
        },
        instantClonePools: workload.workloadDetails.numOfInstClonePools ? workload.workloadDetails.numOfInstClonePools : 0,
        linkedPools: workload.workloadDetails.numOfLinkedClonePools ? workload.workloadDetails.numOfLinkedClonePools : 0,
      };
      break;
    case 'FS':
      vmProfile.shares = {
        count: 1000,
        storage: {
          value: 100,
          units: 'GB',
        },
      };
      break;
  }
  if ('autoMode' in workload.workloadDetails) {
    vmProfile['autoMode'] = workload.workloadDetails.autoMode as unknown as boolean;
  }
  return vmProfile;
}

/**
 * Ported as-is from PCSSizingEngineRequestAdapterService.buildIOProfilePayload
 * (pcs-sizing-engine-request-adapter.service.ts:846-915).
 */
export function buildIOProfilePayload(workloadDetails: VCPM.WorkloadDetails): PcsSizingEngineRequest.IoProfile {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ioProfile: any = {
    accessPattern: workloadDetails.ioAccessPattern,
    size: {
      value: workloadDetails.ioSize,
      units: 'KB',
    },
  };
  if ((workloadDetails.ioAccessPattern).toLocaleLowerCase() === ('Sequential').toLocaleLowerCase()) {
    const ioRatioSplit: string[] = (workloadDetails.ioRatio).split('%');
    if ((workloadDetails.ioRatio).includes('Read')) {
      ioProfile['read'] = {
        ratio: {
          value: ioRatioSplit[0],
          units: '%',
        },
        max: {
          value: 10000,
        },
        throughput: {
          value: 500,
        },
      };
    } else if ((workloadDetails.ioRatio).includes('Write')) {
      ioProfile['write'] = {
        ratio: {
          value: ioRatioSplit[0],
          units: '%',
        },
        max: {
          value: 5000,
        },
        throughput: {
          value: 250,
        },
      };
    }
  } else {
    const ioRatioSplit: string[] = (workloadDetails.ioRatio).split('/');
    const ioRead: number = parseInt(ioRatioSplit[0]);
    const ioWrite: number = parseInt(ioRatioSplit[1]);
    const ioReadObject = {
      ratio: {
        value: ioRead,
        units: '%',
      },
      max: {
        value: 10000,
      },
      throughput: {
        value: 500,
      },
    };
    const ioWriteObject = {
      ratio: {
        value: ioWrite,
        units: '%',
      },
      max: {
        value: 5000,
      },
      throughput: {
        value: 250,
      },
    };
    ioProfile['read'] = ioReadObject;
    ioProfile['write'] = ioWriteObject;
  }
  return ioProfile;
}

/**
 * Module-private, not exported - ported as-is from
 * pcs-sizing-engine-request-adapter.service.ts:917-936.
 *
 * Gap H: the original private getSubcomponentIds(subGroupName) resolved `project` via a fresh
 * `this.store.selectSnapshot(ProjectDetailsState.getCurrentProject)` call and then called the
 * Angular-free DomainInputConfigurationFactoryService.getConfiguration(product, version,
 * architecture, DOMAIN_TYPE.ManagementDomain) itself. The factory is deterministic given those 4
 * inputs, so the caller now resolves `.configuration` ahead of time and passes it in directly -
 * this function only does the subGroup/componentSizing/overheads traversal.
 */
export function getSubcomponentIds(subGroupName: string, managementDomainInputConfig: VcfDomain.Group | null): string[] {
  try {
    const groupCfg = managementDomainInputConfig;
    const group = groupCfg?.subGroups?.find((sg) => (sg.subGroupId || '').toString().toLowerCase() === subGroupName.toLowerCase());
    const sizing = group?.inputs?.find((i) => i.id === 'sizing')?.componentSizing || [];
    const ids = new Set<string>();
    sizing.forEach((sz) => (sz.overheads || []).forEach((oh) => {
      if (oh?.id) {
        ids.add(String(oh.id));
      }
    }));
    return Array.from(ids);
  } catch {
    return [];
  }
}

/**
 * Check if any workload domain has a stretched cluster configuration.
 * Module-private, not exported - ported as-is from
 * pcs-sizing-engine-request-adapter.service.ts:943-953.
 *
 * Detects stretched clusters INSIDE the request builder (management domain layout fallback) -
 * distinct from S8's output-component stretched-cluster multiplier functions, which is a separate,
 * intentionally-not-unified detection path per plan Section 1.5.
 */
export function hasStretchedClusterInWorkloadDomains(project: VCPM.Project): boolean {
  if (!project.data.workloadDomains) {
    return false;
  }

  return project.data.workloadDomains.some((domain) =>
    domain.workloadList?.some((workload) => isWorkloadStretchedByInput(workload)),
  );
}
